/**
 * Per-note local draft persistence.
 *
 * When offline (no DB autosave), edits to the open note are held here so they
 * survive reloads and flush to the DB on reconnect. This is scoped to a single
 * open note — it is NOT the general offline mutation queue (Phase 4.11).
 */

export interface NoteDraft {
  note_title: string;
  note_content: string;
  /** ISO timestamp of the local edit. */
  updated_at: string;
}

const DRAFT_PREFIX = 'note_draft_';

function draftKey(noteId: string): string {
  return `${DRAFT_PREFIX}${noteId}`;
}

/** Writes a draft for a note. Fails silently on quota/availability errors. */
export function writeNoteDraft(noteId: string, draft: NoteDraft): void {
  try {
    localStorage.setItem(draftKey(noteId), JSON.stringify(draft));
  } catch {
    // Best-effort; drafts are non-critical.
  }
}

/** Reads a note's draft, or null if none / unreadable. */
export function readNoteDraft(noteId: string): NoteDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(noteId));
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.note_title === 'string' &&
      typeof parsed.note_content === 'string' &&
      typeof parsed.updated_at === 'string'
    ) {
      return parsed as NoteDraft;
    }
    return null;
  } catch {
    return null;
  }
}

/** Removes a note's draft. */
export function clearNoteDraft(noteId: string): void {
  try {
    localStorage.removeItem(draftKey(noteId));
  } catch {
    // Best-effort.
  }
}

export type DraftReconcileResult =
  | { winner: 'draft'; draft: NoteDraft }
  | { winner: 'server' };

/**
 * Decides whether a local draft should overwrite the server copy.
 *
 * The draft wins only if it is strictly newer than the server's `last_modified`.
 * A malformed/missing timestamp on either side defers to the server (safer:
 * never silently clobber persisted content with an unclamped draft).
 */
export function chooseNewer(
  draft: NoteDraft | null,
  serverLastModified: string,
): DraftReconcileResult {
  if (!draft) return { winner: 'server' };

  const draftTime = Date.parse(draft.updated_at);
  const serverTime = Date.parse(serverLastModified);

  if (Number.isNaN(draftTime) || Number.isNaN(serverTime)) {
    return { winner: 'server' };
  }

  return draftTime > serverTime
    ? { winner: 'draft', draft }
    : { winner: 'server' };
}
